import { useApolloClient } from '@apollo/client'
import { fromUnixTime, isFuture } from 'date-fns'
import jwtDecode from 'jwt-decode'
import { useNavigate } from 'react-router-dom'
import { create as createStore } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { clearPermissionsCache, wipePermissionsCache } from './usePermissions'

export const TOKEN_NAME = 'givewise_auth_state'
export const IMPERSONATOR_TOKEN_NAME = 'givewise_impersonator_token'

const useTokenStore = createStore(
  persist(
    (set, get) => ({
      rawToken: undefined,
      payload: undefined,
      hasShownPendingInvitePopup: false,
      markPendingInvitePopupShown: () => set({ hasShownPendingInvitePopup: true }),
      resetPendingInvitePopupShown: () => set({ hasShownPendingInvitePopup: false }),
      setToken: (token) => {
        let payload
        try {
          payload = token ? jwtDecode(token) : null
        } catch (error) {
          // eslint-disable-next-line no-console -- log intentionally silent error
          console.error('jwtDecode failed', error)
        }

        return set({ rawToken: token, payload })
      },
      // Token stack to support nested impersonation (e.g., Admin -> Advisor -> Client)
      impersonatorTokenStack: [],
      pushImpersonator: (token) =>
        set((state) => ({ impersonatorTokenStack: [...state.impersonatorTokenStack, token] })),
      popImpersonator: () => {
        const stack = get().impersonatorTokenStack
        if (stack.length === 0) return undefined
        const token = stack[stack.length - 1]
        set({ impersonatorTokenStack: stack.slice(0, -1) })
        return token
      },
      clearImpersonatorStack: () => set({ impersonatorTokenStack: [] }),
    }),
    { name: TOKEN_NAME, storage: createJSONStorage(() => localStorage) }
  )
)

{
  // Pull the token from the url (used by the mobile app to auto-log-in in webviews)
  const initialSearchParams = new URLSearchParams(window.location.search)
  const initialToken = initialSearchParams.get('token')
  if (initialToken) {
    useTokenStore.getState().setToken(initialToken)
  }
}

const useToken = () => {
  const token = useTokenStore((state) => state.rawToken)
  return token
}

export function getAuthToken() {
  return useTokenStore.getState().rawToken
}

export function getImpersonatorToken() {
  const stack = useTokenStore.getState().impersonatorTokenStack
  return stack.length > 0 ? stack[stack.length - 1] : undefined
}

function useImpersonation({ login, currentToken }) {
  const impersonatorTokenStack = useTokenStore((state) => state.impersonatorTokenStack)
  const pushImpersonator = useTokenStore((state) => state.pushImpersonator)
  const popImpersonator = useTokenStore((state) => state.popImpersonator)

  async function impersonate(newToken) {
    // Push current token onto stack before impersonating
    // This supports nested impersonation (e.g., Admin -> Advisor -> Client)
    pushImpersonator(currentToken)
    // Clear cached permissions so they are re-fetched for the impersonated user
    clearPermissionsCache()
    return login(newToken)
  }
  async function switchBack() {
    // Pop the previous token from the stack and login as that user
    const previousToken = popImpersonator()
    if (!previousToken) return
    // Clear cached permissions so they are re-fetched for the previous user
    clearPermissionsCache()
    await login(previousToken)
  }
  return [{ isImpersonating: impersonatorTokenStack.length > 0 }, { impersonate, switchBack }]
}

export const useAuth = () => {
  const token = useToken()
  const setToken = useTokenStore((state) => state.setToken)
  const clearImpersonatorStack = useTokenStore((state) => state.clearImpersonatorStack)
  const hasShownPendingInvitePopup = useTokenStore((state) => state.hasShownPendingInvitePopup)
  const markPendingInvitePopupShown = useTokenStore((state) => state.markPendingInvitePopupShown)
  const resetPendingInvitePopupShown = useTokenStore((state) => state.resetPendingInvitePopupShown)
  let auth = null

  if (token) {
    try {
      auth = jwtDecode(token)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('failed jwtDecode', error)
    }
  }

  const isLoggedIn = auth?.exp && isFuture(fromUnixTime(auth?.exp))
  const isAdmin = auth?.admin

  const client = useApolloClient()
  const navigate = useNavigate()
  async function login(newToken) {
    setToken(newToken)
    wipePermissionsCache() // Ensure no stale permissions from a previous user survive
    resetPendingInvitePopupShown() // A fresh sign-in should surface the pending-invite popup again
    await client.resetStore() // Clear cache AND refetch all active queries with new auth context
  }
  const [{ isImpersonating }, { impersonate, switchBack }] = useImpersonation({ login, currentToken: token })
  async function logout({ skipNavigation = false } = {}) {
    wipePermissionsCache() // Wipe without bump — components are unmounting, no re-fetch needed

    if (isImpersonating) {
      // Check if the original user (impersonator) is an admin before switching back
      const impersonatorToken = getImpersonatorToken()
      let impersonatorIsAdmin = false
      if (impersonatorToken) {
        try {
          const impersonatorPayload = jwtDecode(impersonatorToken)
          impersonatorIsAdmin = impersonatorPayload?.admin
        } catch (error) {
          // Silent fail, default to regular dashboard
        }
      }

      // Navigate BEFORE switching back to avoid the current portal's guard
      // reacting to the token change and flashing a 404
      const returnPath = impersonatorIsAdmin ? '/management' : '/funds'
      if (!skipNavigation) {
        navigate(returnPath, { replace: true })
      }

      // Switch back to the original user (sets token + resets Apollo store)
      await switchBack()
    } else {
      await client.clearStore()
      setToken(null)
      clearImpersonatorStack() // Always clear impersonator stack on full logout
      resetPendingInvitePopupShown()
      if (!skipNavigation) {
        navigate('/login')
      }
    }

    localStorage.removeItem('ExternalLinkHideModalPreference')
  }

  // Get impersonation details from token
  const impersonationType = auth?.impersonationType // 'ADMIN' or 'ADVISOR'
  const customPermissions = auth?.customPermissions || null // Granular advisor permission flags

  return [
    {
      isLoggedIn,
      isAdmin,
      isImpersonating,
      impersonationType,
      customPermissions,
      userId: auth?.sub ?? null,
      hasShownPendingInvitePopup,
    },
    { login, logout, impersonate, switchBack, markPendingInvitePopupShown },
  ]
}
